import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { getProfile, updateNotifications } from '@/lib/profileApi';
import { Bell, Briefcase, Clock, Megaphone, Shield } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

const NOTIFICATION_GROUPS = [
  {
    key: 'notification_job_updates',
    icon: Briefcase,
    title: 'Job Updates',
    description: 'Receive emails when a job status changes, a review is requested, or a filing is confirmed.',
  },
  {
    key: 'notification_filing_reminders',
    icon: Clock,
    title: 'Filing Reminders',
    description: 'Get reminders about upcoming filing deadlines and overdue alerts for your companies.',
  },
  {
    key: 'notification_security_alerts',
    icon: Shield,
    title: 'Security Alerts',
    description: 'Be notified about new sign-ins from unrecognised devices and password changes.',
    locked: true,
  },
  {
    key: 'notification_marketing',
    icon: Megaphone,
    title: 'Product Updates & Announcements',
    description: 'Occasional emails about new features, product improvements, and company news.',
  },
];

const NotificationsTab = () => {
  const [loading, setLoading] = useState(true);
  const [prefs, setPrefs] = useState({
    notification_job_updates: true,
    notification_filing_reminders: true,
    notification_security_alerts: true,
    notification_marketing: false,
  });

  const load = useCallback(async () => {
    try {
      const data = await getProfile();
      setPrefs({
        notification_job_updates: data.notification_job_updates ?? true,
        notification_filing_reminders: data.notification_filing_reminders ?? true,
        notification_security_alerts: data.notification_security_alerts ?? true,
        notification_marketing: data.notification_marketing ?? false,
      });
    } catch (err) {
      toast.error(err.message || 'Failed to load notification preferences');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async (key, checked) => {
    const prev = prefs[key];
    setPrefs(p => ({ ...p, [key]: checked }));
    try {
      await updateNotifications({ [key]: checked });
    } catch (err) {
      setPrefs(p => ({ ...p, [key]: prev }));
      toast.error(err.message || 'Failed to update preference');
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3, 4].map(i => (<div key={i} className="flex items-center gap-4"><Skeleton className="w-10 h-10 rounded-lg" /><div className="flex-1 space-y-2"><Skeleton className="w-40 h-4" /><Skeleton className="w-64 h-3" /></div></div>))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-2">Email Notifications</h3>
        <p className="text-sm text-muted-foreground mb-6">Choose which emails you'd like to receive. Changes are saved automatically.</p>
      </div>

      <div className="space-y-1 divide-y divide-border border border-border rounded-lg">
        {NOTIFICATION_GROUPS.map(group => {
          const Icon = group.icon;
          const checked = prefs[group.key];
          const isLocked = group.locked;

          return (
            <div key={group.key} className="flex items-center gap-4 p-4">
              <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                <Icon className="w-5 h-5 text-muted-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Label htmlFor={group.key} className="text-sm font-medium cursor-pointer">{group.title}</Label>
                  {isLocked && <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">Always on</span>}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">{group.description}</p>
              </div>
              <Checkbox
                id={group.key}
                checked={checked}
                disabled={isLocked}
                onCheckedChange={val => handleToggle(group.key, val)}
                className="shrink-0"
              />
            </div>
          );
        })}
      </div>

      <p className="text-xs text-muted-foreground flex items-center gap-1">
        <Bell className="w-3.5 h-3.5" />
        Security alerts cannot be disabled to protect your account.
      </p>
    </div>
  );
};

export default NotificationsTab;
